import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useAuthStore } from "../../store/authStore";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { Skeleton } from "../../components/UI";
import { useFormat } from "../../hooks/useFormat";
import { FiUserPlus, FiArrowLeft, FiCheck, FiClock, FiAlertCircle, FiLock, FiUserX } from "react-icons/fi";
import toast from "react-hot-toast";
import { Page, Action, usePoll, request } from "./shared";
import PlayerHero from "../Profile/PlayerHero";

const Surface = styled.section`
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
    margin-bottom: 12px;
  }
  dl {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 20px 24px;
  }
  dt {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  dd {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  dd.none {
    font-size: 18px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
  }
  p.none {
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;
const Problem = styled.div`
  display: grid;
  justify-items: start;
  gap: 8px;
  padding: 32px 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  svg {
    width: 28px;
    height: 28px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  h1 {
    font-size: 22px;
    line-height: 30px;
    font-weight: 650;
  }
  p {
    max-width: 52ch;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }
`;
const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  justify-self: start;
  font-size: 15px;
  font-weight: 600;
  &&& {
    color: ${(p) => p.theme.colors.textSecondary};
  }
  &&&:hover {
    color: ${(p) => p.theme.colors.text};
  }
`;

export default function PublicProfile() {
  const { userId } = useParams();
  return <ProfileContent key={userId} userId={userId!} />;
}

function ProfileContent({ userId }: { userId: string }) {
  const me = useAuthStore((s) => s.user?.id);
  const { t } = useLanguage();
  const f = useFormat();
  const nav = useNavigate();
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const {
    data: user,
    error,
    errorStatus,
    setData: setUser,
    reload,
  } = usePoll<any>("/profiles/" + userId, 30000, sending);
  const { data: stats } = usePoll<any>("/stats/user/" + userId, 30000);
  const relation = user?.relation;
  const locked = sending || !!error || !relation || relation === "friends" || relation === "pending_out";

  async function sendRequest() {
    if (sendingRef.current || locked) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const result = await request("/friends/requests", { userId });
      setUser((current: any) => ({ ...current, relation: result.relation }));
      toast.success(result.relation === "friends" ? t("pp.now_friends") : t("pp.request_sent"), { id: "profile-friend-" + userId });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  const back = (
    <Back
      to="/"
      onClick={(e) => {
        if (window.history.length > 1) {
          e.preventDefault();
          nav(-1);
        }
      }}
    >
      <FiArrowLeft aria-hidden="true" /> {t("pp.back")}
    </Back>
  );

  /* Ошибки различаются по реальному ответу: нет игрока, нет доступа, сбой загрузки. */
  if (error && !user) {
    const missing = errorStatus === 404;
    const forbidden = errorStatus === 401 || errorStatus === 403;
    return (
      <Page>
        {back}
        <Problem role="alert">
          {missing ? <FiUserX aria-hidden="true" /> : forbidden ? <FiLock aria-hidden="true" /> : <FiAlertCircle aria-hidden="true" />}
          <h1>{missing ? t("pp.not_found") : forbidden ? t("pp.forbidden") : t("pp.load_failed")}</h1>
          <p>{missing ? t("pp.not_found_text") : forbidden ? t("pp.forbidden_text") : t("pp.load_failed_text")}</p>
          <div className="actions">
            {!missing && !forbidden && (
              <Action type="button" $variant="primary" onClick={reload}>
                {t("common.retry")}
              </Action>
            )}
            <Action type="button" onClick={() => nav("/")}>
              {t("match.to_home")}
            </Action>
          </div>
        </Problem>
      </Page>
    );
  }

  const total = stats ? stats.totalMatches ?? stats.total_matches : undefined;
  const kd = stats?.average_kd ?? stats?.kd;

  return (
    <Page>
      {back}
      {!user ? (
        <div role="status" aria-label={t("common.loading")}>
          <Skeleton $h={180} $r={16} />
        </div>
      ) : (
        <>
          <PlayerHero
            name={user.username}
            avatar={user.avatar}
            background={user.background}
            isPremium={!!user.isPremium}
            elo={user.elo}
            actions={
              me !== userId && (
                <Action
                  $variant={relation === "none" || relation === "pending_in" ? "primary" : undefined}
                  disabled={locked}
                  aria-busy={sending}
                  onClick={sendRequest}
                >
                  {relation === "friends" ? <FiCheck aria-hidden="true" /> : relation === "pending_out" ? <FiClock aria-hidden="true" /> : <FiUserPlus aria-hidden="true" />}
                  {sending
                    ? t("pp.sending")
                    : relation === "pending_out"
                      ? t("pp.request_sent")
                      : relation === "friends"
                        ? t("pp.already_friends")
                        : relation === "pending_in"
                          ? t("pp.accept")
                          : relation === "none"
                            ? t("home.add_friend")
                            : t("common.loading")}
                </Action>
              )
            }
          />
          {error && <p role="alert">{error}</p>}
          <Surface aria-labelledby="pp-stats">
            <h2 id="pp-stats">{t("pp.stats")}</h2>
            {!stats ? (
              <Skeleton $h={60} $r={12} />
            ) : total ? (
              <dl>
                <div>
                  <dt>{t("stats.total_matches")}</dt>
                  <dd>{f.number(total, 0)}</dd>
                </div>
                <div>
                  <dt>{t("stats.kd_ratio")}</dt>
                  <dd className={kd ? undefined : "none"}>{kd ? f.number(kd, 2) : t("stats.no_data")}</dd>
                </div>
              </dl>
            ) : (
              <p className="none">{t("pp.no_stats")}</p>
            )}
          </Surface>
        </>
      )}
    </Page>
  );
}
